'use client';

import { useSyncExternalStore, useCallback } from 'react';

import { OfflineFallback } from './offline-fallback';

function subscribeToNetwork(callback: () => void) {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}

function getNetworkSnapshot() {
  return navigator.onLine;
}

function getServerSnapshot() {
  return true; // Assume online during SSR
}

/**
 * Monitors network connectivity and renders an OfflineFallback overlay
 * when the device goes offline. Uses browser online/offline events,
 * which work in both regular browsers and Capacitor WebView.
 *
 * The overlay renders on top of children (not instead of them),
 * so content is preserved when connectivity returns.
 */
export function NetworkStatusProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const isOnline = useSyncExternalStore(
    subscribeToNetwork,
    getNetworkSnapshot,
    getServerSnapshot,
  );

  const handleRetry = useCallback(() => {
    if (navigator.onLine) {
      // Browser already considers itself online — dispatch to update state
      window.dispatchEvent(new Event('online'));
    } else {
      // Probe connectivity by fetching a known-small static asset.
      fetch('/images/favicon/favicon.ico', { method: 'HEAD', cache: 'no-store' })
        .then(() => window.dispatchEvent(new Event('online')))
        .catch(() => {
          // Still offline — no-op, user can tap retry again
        });
    }
  }, []);

  return (
    <>
      {children}
      {!isOnline && <OfflineFallback onRetry={handleRetry} />}
    </>
  );
}
